import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCheckCircle, FaChartLine, FaUserMd, FaRegSmile, FaArrowRight } from "react-icons/fa";

const stats = [
  { label: "Reduction in Paperwork", value: "-60%", icon: <FaChartLine className="text-green-500 text-3xl" /> },
  { label: "Documentation Accuracy", value: "+45%", icon: <FaCheckCircle className="text-blue-500 text-3xl" /> },
  { label: "Staff Time Saved", value: "15 hrs/wk", icon: <FaRegSmile className="text-yellow-500 text-3xl" /> },
];

const timeline = [
  { step: "Initial Challenge", desc: "Maplewood SNF was overwhelmed by paperwork and documentation errors." },
  { step: "Assessment", desc: "A workflow analysis revealed bottlenecks and redundant processes." },
  { step: "Goal Setting", desc: "The goal was to cut paperwork by 50% and improve documentation accuracy." },
  { step: "Vendor Selection", desc: "RoomRounds was chosen for its digital documentation and workflow tools." },
  { step: "Staff Training", desc: "Staff received training on digital documentation and mobile workflows." },
  { step: "Implementation", desc: "RoomRounds was rolled out, digitizing all rounds and care notes." },
  { step: "Monitoring", desc: "Supervisors tracked documentation accuracy and time savings." },
  { step: "Continuous Improvement", desc: "Weekly feedback sessions helped refine digital workflows." },
  { step: "Results", desc: "Paperwork was reduced by 60%, and documentation accuracy improved by 45%." },
  { step: "Sustained Success", desc: "Digital documentation is now the standard, saving staff 15 hours per week." },
];

const CaseStudy4: React.FC = () => (
  <motion.section initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, type: "spring" }} className="max-w-4xl mx-auto py-12 px-4">
    {/* Hero Section */}
    <div className="relative rounded-3xl overflow-hidden mb-10 shadow-lg">
      <img src="https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=1000&q=80" alt="Case Study Hero" className="w-full h-64 object-cover object-center opacity-80" />
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-blue-700/60 to-blue-400/30 flex flex-col justify-end p-8">
        <h1 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow mb-2">Case Study: Digital Documentation & Workflow</h1>
        <div className="text-blue-100 text-sm mb-2">By RoomRounds Team | June 2024</div>
      </div>
    </div>
    {/* Stats Cards */}
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
      {stats.map((stat, i) => (
        <div key={i} className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center hover:shadow-xl transition">
          {stat.icon}
          <div className="text-3xl font-extrabold text-blue-800 mt-2 mb-1">{stat.value}</div>
          <div className="text-gray-600 text-sm font-semibold">{stat.label}</div>
        </div>
      ))}
    </motion.div>
    {/* Timeline / Process */}
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-blue-700 mb-6">Journey to Digital Excellence</h2>
      <ol className="relative border-l-2 border-blue-200 ml-4">
        {timeline.map((item, idx) => (
          <motion.li key={item.step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * idx }} className="mb-10 ml-6">
            <span className="absolute -left-4 flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full ring-4 ring-white text-white font-bold text-lg shadow">{idx + 1}</span>
            <h3 className="font-bold text-blue-800 text-lg mb-1">{item.step}</h3>
            <p className="text-gray-700 text-base">{item.desc}</p>
          </motion.li>
        ))}
      </ol>
    </div>
    {/* Testimonial */}
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-blue-50 rounded-2xl shadow p-8 mb-12 flex flex-col md:flex-row items-center gap-6">
      <FaUserMd className="text-5xl text-blue-400 mb-4 md:mb-0" />
      <div>
        <blockquote className="text-lg italic text-blue-900 mb-2">“RoomRounds made paperwork a thing of the past. Our staff is happier and more efficient than ever.”</blockquote>
        <div className="text-blue-700 font-semibold">Director of Nursing, Maplewood SNF</div>
      </div>
    </motion.div>
    {/* Call to Action */}
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-center mb-8">
      <h2 className="text-2xl font-bold text-blue-800 mb-2">Ready to Transform Your Facility?</h2>
      <p className="text-gray-700 mb-4">See how RoomRounds can help you digitize documentation and save time.</p>
      <Link to="/contact" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition font-bold text-lg">
        Schedule a Demo <FaArrowRight />
      </Link>
    </motion.div>
    <div className="text-center">
      <Link to="/resources" className="text-blue-600 hover:underline">← Back to Resources</Link>
    </div>
  </motion.section>
);

export default CaseStudy4; 